import React, { useState } from 'react';
import { X } from 'react-feather';
import PropTypes from 'prop-types';
import { Button } from 'ui';
import Notice, { NOTICE_TYPES } from 'ui/Notice/Notice';
import { Text, TEXT_COLORS, TEXT_VARIANTS } from 'ui/Typography';
import MediaPlayer from 'components/ui/MediaPlayer';
import AutomatedAnalysisContainer from 'components/VideoResponse/Grading/Modal/AutomatedAnalysis/AutomatedAnalysisContainer';
import { hasFeatureFlags } from 'utils/features';
import VideoDownloadButton from 'components/shared/UniversalDownloadButton/VideoDownloadButton';
import LoadingIcon from 'components/shared/UniversalDownloadButton/LoadingIcon';
import PostToChannelLinkButton from 'components/shared/PostToChannelLinkButton';
import GraderFeedback from './GraderFeedback';

const VideoResponseModal = props => {
  const {
    usePubSub = true,
    data,
    setCorrect,
    setFeedback: setFeedbackProp,
    closeModal,
    criteriaRatings,
    ratingOptions,
    apiKey,
    feedbackCriteriaPresent,
    hasZiggeoForAllMedia = true,
    displayFeedbackPanel = true,
    currentUserId,
    isAdmin,
    isLearnerView,
    showPostToChannelLinkButton,
    automatedAnalysis,
    enableAutomatedAnalysis,
  } = props;
  const [correctAnswer, setCorrectAnswer] = useState(props.correctAnswer);
  const [feedback, setFeedback] = useState(props.feedback);

  const saveGradeResponse = () => {
    setCorrect(data.id, correctAnswer);
    if (usePubSub) {
      global.pubsub.publish('gradingTable:correct', data.id, correctAnswer);
    }

    setFeedbackProp(data.id, feedback);
    if (usePubSub) {
      global.pubsub.publish('gradingTable:feedback', data.id, feedback);
    }

    closeModal();
  };

  const handleCorrectClick = e => {
    setCorrectAnswer(e.target.value);
  };

  const handleFeedbackUpdate = e => {
    setFeedback(e.target.value);
  };

  const multilanguageAutomatedAnalysisEnabled = hasFeatureFlags([
    'readiness-enable-automated-multilanguage-assesment',
  ]);

  const turnedOffText = isLearnerView
    ? t('automated_analysis.privacy_policy.learner_turned_off')
    : t('automated_analysis.privacy_policy.turned_off');

  const isSeismicProvider = data.recording?.provider === 'seismic';

  return (
    <div className="ModalTakeover-container">
      <button
        type="button"
        className="u-resetBtn ModalTakeover-closeButton"
        aria-label={t('common.close')}
        onClick={saveGradeResponse}
      >
        <X />
      </button>
      <div className="VideoResponseModal-body">
        <div className="VideoResponseModal-userResponse f-1">
          <div className="VideoResponseModal-information d-f fd-c">
            <Text className="pb-16" variant={TEXT_VARIANTS.TITLE}>
              {data.question}
            </Text>
            <Text variant={TEXT_VARIANTS.BODY}>{data.currentUser}</Text>
            {multilanguageAutomatedAnalysisEnabled &&
              data.questionRequestedLanguage &&
              enableAutomatedAnalysis !== false &&
              automatedAnalysis && (
                <Text
                  className="bg-secondary bdrs-base p-4 mt-8 w-fc"
                  variant={TEXT_VARIANTS.TAG_BOLD_ON_DARK}
                  color={TEXT_COLORS.ON_SECONDARY}
                >
                  {data.questionRequestedLanguage}
                </Text>
              )}
          </div>
          {data.recording !== null && (
            <MediaPlayer
              hasZiggeoForAllMedia={hasZiggeoForAllMedia}
              recording={data.recording}
              ziggeoApiKey={apiKey}
              customClass="Video Video--playable"
              audioType={
                data.questionType === 'audio_recording' ? 'Recording' : null
              }
            />
          )}
          {isAdmin && isSeismicProvider && !isLearnerView && (
            <div className="mt-3">
              <div className="d-if f-r ta-l">
                <VideoDownloadButton
                  item={{
                    repository: 'Practice',
                    type: 'ExternalVideo',
                    id: data.recording?.externalId,
                  }}
                  isPostToChannels={showPostToChannelLinkButton}
                  currentUserId={currentUserId}
                  hocOptions={{
                    Loading: LoadingIcon({
                      isPostToChannels: showPostToChannelLinkButton,
                    }),
                  }}
                />
                {showPostToChannelLinkButton && (
                  <PostToChannelLinkButton
                    channelLink={data.recording.postToChannelLink}
                  />
                )}
              </div>
            </div>
          )}
          {data.questionAutomatedAnalysis && (
            <>
              {data.enableAutomatedAnalysis !== false ? (
                <AutomatedAnalysisContainer
                  data={data}
                  currentUserId={currentUserId}
                />
              ) : (
                <div className="TemplateCreationModal-scormNotice d-ib">
                  <Notice
                    iconName="alert"
                    type={NOTICE_TYPES.INFO}
                    bodyText={turnedOffText}
                  />
                </div>
              )}
            </>
          )}
        </div>
        {data.gradable && displayFeedbackPanel && (
          <div className="VideoResponseModal-form">
            <GraderFeedback
              correctAnswer={correctAnswer}
              feedback={feedback}
              preview={data.preview}
              answerId={data.id}
              handleCorrectClick={handleCorrectClick}
              handleFeedbackUpdate={handleFeedbackUpdate}
              criteriaRatings={criteriaRatings}
              ratingOptions={ratingOptions}
              feedbackCriteriaPresent={feedbackCriteriaPresent}
            />
            {!data.preview && (
              <div className="d-f fd-rr mt-16">
                <Button onClick={saveGradeResponse} customColor>
                  {t('common.save')}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

VideoResponseModal.propTypes = {
  usePubSub: PropTypes.bool,
  data: PropTypes.shape({
    id: PropTypes.number,
    preview: PropTypes.bool,
  }),
  setCorrect: PropTypes.func,
  setFeedback: PropTypes.func,
  closeModal: PropTypes.func,
  criteriaRatings: PropTypes.arrayOf(
    PropTypes.shape({
      description: PropTypes.string,
      criterion_id: PropTypes.number,
    }),
  ),
  ratingOptions: PropTypes.arrayOf(
    PropTypes.shape({
      description: PropTypes.string,
      score: PropTypes.number,
    }),
  ),
  apiKey: PropTypes.string,
  feedbackCriteriaPresent: PropTypes.bool,
  displayFeedbackPanel: PropTypes.bool,
};

export default props => <VideoResponseModal {...props} />;
